import { useState, useEffect, useMemo } from 'react';
import { db } from '../db';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Search, Star, AlertTriangle, ChevronLeft, RotateCcw, Heart, X, Volume2 } from 'lucide-react';
import allPhrases from '../data/phrases';
import { LANGUAGES, CATEGORIES, detectLanguageFromDestination } from '../data/languageConfig';

// Web Speech API language codes
const SPEECH_LANG_CODES = {
  spanish: 'es-ES',
  french: 'fr-FR',
  portuguese: 'pt-BR',
  german: 'de-DE',
  italian: 'it-IT',
  thai: 'th-TH',
  vietnamese: 'vi-VN',
  japanese: 'ja-JP',
  arabic: 'ar-SA',
  mandarin: 'zh-CN',
};

// Pre-load voices on module load
let voicesLoaded = false;
if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
  const loadVoices = () => {
    window.speechSynthesis.getVoices();
    voicesLoaded = true;
  };
  loadVoices();
  window.speechSynthesis.onvoiceschanged = loadVoices;
}

const speakPhrase = (text, langKey) => {
  if (!('speechSynthesis' in window)) return;
  
  // Cancel any ongoing speech immediately
  window.speechSynthesis.cancel();
  
  const langCode = SPEECH_LANG_CODES[langKey] || 'en-US';
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = langCode;
  utterance.rate = 0.8;
  utterance.pitch = 1;
  utterance.volume = 1;
  
  // Try to match a voice
  const voices = window.speechSynthesis.getVoices();
  const langPrefix = langCode.split('-')[0];
  const match = voices.find(v => v.lang.startsWith(langPrefix));
  if (match) utterance.voice = match;
  
  // Chrome Android bug: speech can get stuck. Resume trick.
  window.speechSynthesis.speak(utterance);
  
  // Chrome has a bug where long utterances pause — keep it alive
  const keepAlive = setInterval(() => {
    if (!window.speechSynthesis.speaking) {
      clearInterval(keepAlive);
    } else {
      window.speechSynthesis.pause();
      window.speechSynthesis.resume();
    }
  }, 5000);
  
  utterance.onend = () => clearInterval(keepAlive);
  utterance.onerror = () => clearInterval(keepAlive);
};

const LanguageScreen = () => {
  const [selectedLang, setSelectedLang] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [favorites, setFavorites] = useState([]);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [emergencyMode, setEmergencyMode] = useState(false);
  const [showToLocal, setShowToLocal] = useState(null);
  const [speakingId, setSpeakingId] = useState(null);
  const [showLangPicker, setShowLangPicker] = useState(false);

  useEffect(() => {
    loadDestinationLanguage();
    loadFavorites();
    // Pre-load speech voices
    if ('speechSynthesis' in window) {
      window.speechSynthesis.getVoices();
    }
  }, []);

  const handleSpeak = (phrase) => {
    setSpeakingId(phrase.id);
    speakPhrase(phrase.t, selectedLang);
    // Reset indicator after a delay
    setTimeout(() => setSpeakingId(null), 3000);
  };

  const loadDestinationLanguage = async () => {
    const dest = await db.settings.get('destination');
    if (dest?.value) {
      const detected = detectLanguageFromDestination(dest.value);
      setSelectedLang(detected || 'spanish');
    } else {
      setSelectedLang('spanish');
    }
  };

  const loadFavorites = async () => {
    try {
      const favs = await db.phraseFavorites.toArray();
      setFavorites(favs.map(f => f.phraseId));
    } catch (e) {}
  };

  const toggleFavorite = async (phrase, lang) => {
    const isFav = favorites.includes(phrase.id);
    if (isFav) {
      await db.phraseFavorites.where('phraseId').equals(phrase.id).delete();
      setFavorites(prev => prev.filter(id => id !== phrase.id));
    } else {
      await db.phraseFavorites.add({
        language: lang,
        phraseId: phrase.id,
        phrase: phrase.e,
        translation: phrase.t,
        pronunciation: phrase.p,
        category: selectedCategory || 'favorites'
      });
      setFavorites(prev => [...prev, phrase.id]);
    }
  };

  const currentPhrases = useMemo(() => {
    if (!selectedLang || !allPhrases[selectedLang]) return [];
    const langData = allPhrases[selectedLang];

    let phrases = [];

    if (emergencyMode) {
      // Collect all high-priority phrases across all categories
      Object.entries(langData).forEach(([cat, items]) => {
        items.forEach(p => { if (p.pri === 'high') phrases.push({ ...p, _cat: cat }); });
      });
      return phrases;
    }

    if (showFavoritesOnly) {
      Object.entries(langData).forEach(([cat, items]) => {
        items.forEach(p => { if (favorites.includes(p.id)) phrases.push({ ...p, _cat: cat }); });
      });
      return phrases;
    }

    if (selectedCategory) {
      phrases = (langData[selectedCategory] || []).map(p => ({ ...p, _cat: selectedCategory }));
    } else {
      // Search across all categories
      Object.entries(langData).forEach(([cat, items]) => {
        items.forEach(p => phrases.push({ ...p, _cat: cat }));
      });
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      phrases = phrases.filter(p =>
        p.e.toLowerCase().includes(q) ||
        p.t.toLowerCase().includes(q) ||
        p.p.toLowerCase().includes(q)
      );
    }

    return phrases;
  }, [selectedLang, selectedCategory, searchQuery, emergencyMode, showFavoritesOnly, favorites]);

  const langInfo = selectedLang ? LANGUAGES[selectedLang] : null;

  // Emergency Mode Full Screen
  if (emergencyMode) {
    return (
      <div className="min-h-screen bg-red-950 p-4 pb-24" data-testid="emergency-mode">
        <div className="flex items-center justify-between mb-6 pt-4">
          <Button
            onClick={() => setEmergencyMode(false)}
            variant="ghost"
            className="text-white hover:bg-red-900"
            data-testid="exit-emergency-btn"
          >
            <X className="w-5 h-5 mr-1" /> Exit
          </Button>
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <span className="text-red-400 font-bold text-lg">EMERGENCY</span>
          </div>
          <span className="text-white/50 text-sm">{langInfo?.flag} {langInfo?.name}</span>
        </div>

        <div className="space-y-3">
          {currentPhrases.map(phrase => (
            <div key={phrase.id} className="flex gap-2 items-stretch">
              <button
                onClick={() => setShowToLocal(phrase)}
                className="flex-1 bg-red-900/60 border border-red-700/50 rounded-xl p-4 text-left active:scale-[0.98] transition-transform"
                data-testid={`emergency-phrase-${phrase.id}`}
              >
                <p className="text-white/70 text-sm">{phrase.e}</p>
                <p className="text-white text-2xl font-bold mt-1">{phrase.t}</p>
                <p className="text-red-300 text-sm mt-1 italic">{phrase.p}</p>
              </button>
              <button
                onClick={() => handleSpeak(phrase)}
                className={`bg-red-900/60 border border-red-700/50 rounded-xl px-4 flex items-center justify-center active:scale-95 transition-transform ${speakingId === phrase.id ? 'animate-pulse bg-red-800/80' : ''}`}
                data-testid={`speak-emergency-${phrase.id}`}
              >
                <Volume2 className="w-6 h-6 text-red-300" />
              </button>
            </div>
          ))}
        </div>

        {/* Show to Local Modal */}
        {showToLocal && (
          <ShowToLocalOverlay phrase={showToLocal} onClose={() => setShowToLocal(null)} emergency langKey={selectedLang} />
        )}
      </div>
    );
  }

  // Language Picker
  if (showLangPicker) {
    return (
      <div className="p-4 pb-24 animate-in" data-testid="language-picker">
        <div className="pt-6 mb-6">
          <Button onClick={() => setShowLangPicker(false)} variant="ghost" className="mb-2 -ml-2" data-testid="back-from-picker">
            <ChevronLeft className="w-5 h-5 mr-1" /> Back
          </Button>
          <h1 className="text-3xl font-heading font-bold text-foreground">Choose Language</h1>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {Object.entries(LANGUAGES).map(([key, info]) => (
            <button
              key={key}
              onClick={() => { setSelectedLang(key); setShowLangPicker(false); }}
              className={`p-4 rounded-xl border text-left transition-all ${
                selectedLang === key
                  ? 'bg-primary/10 border-primary/40'
                  : 'bg-card border-border hover:border-primary/20'
              }`}
              data-testid={`lang-option-${key}`}
            >
              <span className="text-2xl">{info.flag}</span>
              <p className="font-semibold text-foreground mt-1">{info.name}</p>
              <p className="text-xs text-muted-foreground">{info.native}</p>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Category View (phrase list)
  if (selectedCategory || searchQuery || showFavoritesOnly) {
    const catInfo = CATEGORIES.find(c => c.id === selectedCategory);
    return (
      <div className="p-4 pb-24 animate-in" data-testid="phrase-list-view">
        <div className="pt-6 mb-4">
          <Button
            onClick={() => { setSelectedCategory(null); setSearchQuery(''); setShowFavoritesOnly(false); }}
            variant="ghost" className="mb-2 -ml-2"
            data-testid="back-from-phrases"
          >
            <ChevronLeft className="w-5 h-5 mr-1" /> Back
          </Button>
          <h2 className="text-2xl font-heading font-bold text-foreground">
            {showFavoritesOnly ? 'Favorites' : catInfo?.name || 'Search Results'}
          </h2>
          <p className="text-sm text-muted-foreground">{langInfo?.flag} {langInfo?.name} — {currentPhrases.length} phrases</p>
        </div>

        {/* Search within category */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search phrases..."
            className="pl-10 bg-secondary/50 border-transparent"
            data-testid="phrase-search-input"
          />
        </div>

        <div className="space-y-2">
          {currentPhrases.length === 0 && (
            <p className="text-center text-muted-foreground py-8">No phrases found</p>
          )}
          {currentPhrases.map(phrase => (
            <div
              key={phrase.id}
              className="bg-card border border-border rounded-xl overflow-hidden"
              data-testid={`phrase-card-${phrase.id}`}
            >
              <button
                onClick={() => setShowToLocal(phrase)}
                className="w-full p-4 text-left active:bg-secondary/30 transition-colors"
                data-testid={`show-phrase-${phrase.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground">{phrase.e}</p>
                    <p className="text-lg font-semibold text-foreground mt-0.5">{phrase.t}</p>
                    <p className="text-sm text-primary italic mt-0.5">{phrase.p}</p>
                  </div>
                  {phrase.pri === 'high' && (
                    <span className="bg-red-500/15 text-red-500 text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0">URGENT</span>
                  )}
                </div>
              </button>
              <div className="flex border-t border-border">
                <button
                  onClick={() => handleSpeak(phrase)}
                  className={`flex-1 py-2.5 text-xs font-medium text-foreground flex items-center justify-center gap-1 hover:bg-secondary/30 transition-colors ${speakingId === phrase.id ? 'text-primary animate-pulse' : ''}`}
                  data-testid={`speak-btn-${phrase.id}`}
                >
                  <Volume2 className="w-3.5 h-3.5" />
                  {speakingId === phrase.id ? 'Speaking...' : 'Speak'}
                </button>
                <button
                  onClick={() => toggleFavorite(phrase, selectedLang)}
                  className={`flex-1 py-2.5 text-xs font-medium flex items-center justify-center gap-1 border-l border-border transition-colors ${
                    favorites.includes(phrase.id) ? 'text-amber-500' : 'text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid={`fav-btn-${phrase.id}`}
                >
                  <Star className={`w-3.5 h-3.5 ${favorites.includes(phrase.id) ? 'fill-amber-500' : ''}`} />
                  {favorites.includes(phrase.id) ? 'Saved' : 'Save'}
                </button>
                <button
                  onClick={() => setShowToLocal(phrase)}
                  className="flex-1 py-2.5 text-xs font-medium text-primary flex items-center justify-center gap-1 border-l border-border hover:bg-primary/5 transition-colors"
                  data-testid={`show-local-btn-${phrase.id}`}
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  Show
                </button>
              </div>
            </div>
          ))}
        </div>

        {showToLocal && (
          <ShowToLocalOverlay phrase={showToLocal} onClose={() => setShowToLocal(null)} langKey={selectedLang} />
        )}
      </div>
    );
  }

  // Main Language Screen (categories grid)
  return (
    <div className="p-4 space-y-5 animate-in pb-24" data-testid="language-main-screen">
      <div className="pt-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-foreground tracking-tight">Language</h1>
          <p className="text-muted-foreground mt-1">Offline phrasebook</p>
        </div>
        <button
          onClick={() => setShowLangPicker(true)}
          className="flex items-center gap-2 bg-card border border-border rounded-xl px-4 py-2.5 hover:border-primary/30 transition-colors"
          data-testid="change-language-btn"
        >
          <span className="text-xl">{langInfo?.flag}</span>
          <span className="text-sm font-medium text-foreground">{langInfo?.name}</span>
          <ChevronLeft className="w-4 h-4 text-muted-foreground rotate-[270deg]" />
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={`Search in ${langInfo?.name || 'language'}...`}
          className="pl-10 bg-secondary/50 border-transparent"
          data-testid="main-search-input"
        />
      </div>

      {/* Emergency + Favorites row */}
      <div className="grid grid-cols-2 gap-3">
        <button
          onClick={() => setEmergencyMode(true)}
          className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-left hover:bg-red-500/15 transition-colors active:scale-[0.98]"
          data-testid="emergency-mode-btn"
        >
          <AlertTriangle className="w-6 h-6 text-red-500 mb-2" />
          <p className="font-bold text-foreground">Emergency</p>
          <p className="text-xs text-muted-foreground">Critical phrases</p>
        </button>
        <button
          onClick={() => setShowFavoritesOnly(true)}
          className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-left hover:bg-amber-500/15 transition-colors active:scale-[0.98]"
          data-testid="favorites-btn"
        >
          <Star className="w-6 h-6 text-amber-500 mb-2" />
          <p className="font-bold text-foreground">Favorites</p>
          <p className="text-xs text-muted-foreground">{favorites.length} saved</p>
        </button>
      </div>

      {/* Categories Grid */}
      <div>
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Categories</h3>
        <div className="grid grid-cols-3 gap-2">
          {CATEGORIES.map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className="bg-card border border-border rounded-xl p-3 text-center hover:border-primary/30 transition-all active:scale-[0.97]"
              data-testid={`category-${cat.id}`}
            >
              <i className={`ph-fill ph-${cat.icon} text-primary text-xl mb-1`}></i>
              <p className="text-xs font-medium text-foreground leading-tight">{cat.name}</p>
            </button>
          ))}
        </div>
      </div>

      {showToLocal && (
        <ShowToLocalOverlay phrase={showToLocal} onClose={() => setShowToLocal(null)} langKey={selectedLang} />
      )}
    </div>
  );
};

// Show to Local - Large text overlay
const ShowToLocalOverlay = ({ phrase, onClose, emergency = false, langKey }) => {
  const [speaking, setSpeaking] = useState(false);
  
  const handleModalSpeak = () => {
    setSpeaking(true);
    speakPhrase(phrase.t, langKey);
    setTimeout(() => setSpeaking(false), 3000);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent
        className={`max-w-sm ${emergency ? 'bg-red-950 border-red-800' : 'bg-card border-border'}`}
        data-testid="show-to-local-modal"
      >
        <div className="text-center py-6 space-y-6">
          <p className={`text-sm ${emergency ? 'text-red-300' : 'text-muted-foreground'}`}>{phrase.e}</p>
          <p className={`text-4xl font-bold leading-tight ${emergency ? 'text-white' : 'text-foreground'}`}>
            {phrase.t}
          </p>
          <p className={`text-lg italic ${emergency ? 'text-red-300' : 'text-primary'}`}>{phrase.p}</p>
          <Button
            onClick={handleModalSpeak}
            variant="outline"
            className={`w-full rounded-full ${speaking ? 'animate-pulse' : ''} ${emergency ? 'border-red-700 text-red-300 hover:bg-red-900' : ''}`}
            data-testid="speak-in-modal"
          >
            <Volume2 className="w-5 h-5 mr-2" />
            {speaking ? 'Speaking...' : 'Speak Aloud'}
          </Button>
          <Button
            onClick={onClose}
            className={`w-full rounded-full ${emergency ? 'bg-red-700 hover:bg-red-600 text-white' : ''}`}
            data-testid="close-show-local"
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LanguageScreen;
